import React, { useEffect, useState } from "react";
import { Typography, Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { USE_MOCK_DATA, mockNotes, mockGraphData } from "../utils/mockData";

const ProfilePage = () => {
  const [stats, setStats] = useState({ notes: 0, concepts: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const mockNodes = Array.isArray(mockGraphData?.nodes) ? mockGraphData.nodes : [];
      const uniqueConcepts = new Set(
        mockNodes.map((node) => (node.entity_name || node.label || node.id || "").toLowerCase()).filter(Boolean)
      );
      setStats({
        notes: Array.isArray(mockNotes) ? mockNotes.length : 0,
        concepts: uniqueConcepts.size,
      });
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setStats({ notes: 0, concepts: 0 });
      return;
    }

    Promise.all([
      fetch("https://refinery-note-service-e3btdjbccsbdb5af.australiasoutheast-01.azurewebsites.net/notes", {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch("https://refinery-graph-service-fed6gvbmgzdvbrf0.australiasoutheast-01.azurewebsites.net/graph_data", {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(async ([notesResponse, graphResponse]) => {
        const notesData = notesResponse.ok ? await notesResponse.json() : [];
        const graphData = graphResponse.ok ? await graphResponse.json() : [];

        const noteCount = Array.isArray(notesData) ? notesData.length : 0;
        const graphItems = Array.isArray(graphData) ? graphData : [graphData];
        const uniqueConcepts = new Set();

        graphItems.forEach((graph) => {
          (graph?.nodes || []).forEach((node) => {
            const name = (node.entity_name || node.label || node.id || "").toLowerCase();
            if (name) uniqueConcepts.add(name);
          });
        });

        setStats({
          notes: noteCount,
          concepts: uniqueConcepts.size,
        });
      })
      .catch(() => {
        setStats({ notes: 0, concepts: 0 });
      });
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100%",
        fontFamily: "Roboto, Arial, sans-serif",
        background: "linear-gradient(180deg, #f7f4fb 0%, #ffffff 52%, #f2eef9 100%)",
        pb: isMobile ? "calc(env(safe-area-inset-bottom) + 120px)" : 4,
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box
          sx={{
            position: "absolute",
            width: 220,
            height: 220,
            top: -80,
            right: -40,
            borderRadius: "999px",
            background: "radial-gradient(circle, rgba(92, 59, 139, 0.18) 0%, rgba(92, 59, 139, 0) 70%)",
            opacity: 0.75,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 260,
            height: 260,
            left: -120,
            bottom: 100,
            borderRadius: "999px",
            background: "radial-gradient(circle, rgba(92, 59, 139, 0.18) 0%, rgba(92, 59, 139, 0) 70%)",
            opacity: 0.75,
          }}
        />
      </Box>

      <Box sx={{ position: "relative", maxWidth: 760, mx: "auto", px: { xs: "22px", sm: 3 }, pt: { xs: "16px", sm: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", color: "#492099", fontWeight: 700, fontSize: { xs: 20, sm: 22 }, marginLeft: "-4px", mt: "-2px", mb: 1.2 }}>
          <img src="/logo2-removebg-preview.png" alt="NoteRefinery logo" style={{ width: 42, height: 42, marginRight: 0 }} />
          <span style={{ marginTop: 6 }}>NoteRefinery</span>
        </Box>

        <Box
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(73, 32, 153, 0.12)",
            background: "rgba(255,255,255,0.84)",
            boxShadow: "0 16px 26px rgba(73, 32, 153, 0.08)",
            p: { xs: 2, sm: 2.4 },
          }}
        >
          <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7c6f96", fontWeight: 700, mb: 0.5 }}>
            Support
          </Typography>
          <Typography sx={{ color: "#5e4e7a", fontSize: 14.5, mb: 2 }}>
            Account help and product support.
          </Typography>
          <Typography sx={{ color: "#2d2342", fontSize: 14, fontWeight: 700, mb: 1 }}>
            Your account usage
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.2, mb: 1.4 }}>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(73, 32, 153, 0.1)",
                backgroundColor: "#fff",
                p: 1.6,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#6e5e8d", fontSize: 12, mb: 0.4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Notes
              </Typography>
              <Typography sx={{ color: "#2d2342", fontSize: 26, fontWeight: 800 }}>{stats.notes}</Typography>
            </Box>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(73, 32, 153, 0.1)",
                backgroundColor: "#fff",
                p: 1.6,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#6e5e8d", fontSize: 12, mb: 0.4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Concepts
              </Typography>
              <Typography sx={{ color: "#2d2342", fontSize: 26, fontWeight: 800 }}>{stats.concepts}</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(73, 32, 153, 0.1)",
              backgroundColor: "#fff",
              p: 1.4,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <SupportAgentOutlinedIcon sx={{ color: "#492099", mt: 0.2, fontSize: 20 }} />
            <Box>
              <Typography sx={{ color: "#2d2342", fontSize: 14.5, fontWeight: 700, mb: 0.3 }}>
                Need help with your account?
              </Typography>
              <Typography sx={{ color: "#5e4e7a", fontSize: 13.5, mb: 0.8 }}>
                Send us a quick message and we will help with access, notes, or graph issues.
              </Typography>
              <Button
                component="a"
                href="mailto:maria@littleoxfordstreet.com"
                startIcon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  borderRadius: 99,
                  px: 1.5,
                  py: 0.7,
                  color: "#492099",
                  border: "1px solid rgba(73, 32, 153, 0.25)",
                  background: "rgba(73, 32, 153, 0.04)",
                  fontWeight: 700,
                  fontSize: 13,
                  minWidth: 0,
                }}
              >
                maria@littleoxfordstreet.com
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {isMobile && (
        <>
          <button
            type="button"
            onClick={() => navigate("/newnote")}
            aria-label="Create new note"
            style={{
              position: "fixed",
              left: "calc(50% - 36px)",
              bottom: "calc(env(safe-area-inset-bottom) + 74px)",
              width: 72,
              height: 72,
              borderRadius: "50%",
              border: "none",
              background: "linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 12px 26px rgba(90, 50, 186, 0.38)",
              zIndex: 20,
              cursor: "pointer",
            }}
          >
            <AddIcon sx={{ fontSize: 36 }} />
          </button>
          <nav
            aria-label="Primary"
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              padding: "10px 16px calc(env(safe-area-inset-bottom) + 12px)",
              display: "flex",
              justifyContent: "space-around",
              background: "rgba(255, 255, 255, 0.9)",
              borderTop: "1px solid rgba(18, 50, 53, 0.08)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/app?view=home", { state: { openView: "home" } })}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, border: "none", background: "none", color: "#9a90b4", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
            >
              <SearchIcon fontSize="small" />
              <span>Explore</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/app?view=notes", { state: { openView: "notes" } })}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, border: "none", background: "none", color: "#9a90b4", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
              <span>Notes</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, border: "none", background: "none", color: location.pathname === "/profile" ? "#492099" : "#9a90b4", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
            >
              <SettingsIcon fontSize="small" />
              <span>Support</span>
            </button>
          </nav>
        </>
      )}
    </Box>
  );
};

export default ProfilePage;
